import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { GenericFieldTransformer, OrderFieldTransformer } from './FieldTransformers/FieldTransformers';
import { useFilterController } from './useFilterForm';
import { FiltersControl } from './types';

export type FilterPaginationResult = ReturnType<typeof useFilterPaginagion>;
export type AuxiliaryFilterFields = { limit: string; offset: string; order: FilterOrder };

export interface FilterOrder {
  key: string;
  isAscending: boolean;
}

export const useFilterPaginagion = ({
  control,
  defaultValues = {},
  omitResetKeys = [auxiliaryFields.offset.name, auxiliaryFields.order.name],
}: {
  control: FiltersControl;
  defaultValues?: any;
  omitResetKeys?: string[];
}) => {
  const limit = useFilterController({ control, name: auxiliaryFields.limit.name });
  const offset = useFilterController({ control, name: auxiliaryFields.offset.name });

  const limitValue = Number(limit.field.value);
  const offsetValue = Number(offset.field.value);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      limit.field.onChange({ target: { value } });
      control.flushDebounce();
    },
    [limit.field.onChange]
  );
  const handlePageChange = useCallback(
    (value: number) => {
      offset.field.onChange({ target: { value: (value * limitValue).toString() } });
      control.flushDebounce();
      window.scrollTo(0, 0);
    },
    [offset.field.onChange, limitValue]
  );

  const page = offsetValue / limitValue;

  // // reset page if values changed
  const keysToWatch = useMemo(() => Object.keys(defaultValues).filter(key => !omitResetKeys.includes(key)), [defaultValues]);

  const watch = useWatch({ control, name: keysToWatch });

  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    if (page !== 0) {
      handlePageChange(0);
    }
  }, [watch]);

  const result = useMemo(
    () => ({ handlePageChange, handlePageSizeChange, page, pageSize: limitValue }),
    [handlePageChange, handlePageSizeChange, limitValue, page]
  );

  return result;
};

export const useFilterSortOrder = ({ control, isSearchActive }: { control: FiltersControl; isSearchActive: boolean }) => {
  const orderField = useFilterController({ control, name: auxiliaryFields.order.name });
  const offset = useFilterController({ control, name: auxiliaryFields.offset.name });
  const sortOrder = orderField.field.value as FilterOrder;

  const handleOrderChange = useCallback(
    (key: string) => {
      let result = { key } as FilterOrder;

      if (key === sortOrder.key) {
        result.isAscending = !sortOrder.isAscending;
      } else {
        result.isAscending = true;
      }
      offset.field.onChangeOriginal({ target: { value: 0 } });
      orderField.field.onChange({ target: { value: result } });
      control.flushDebounce();
    },
    [orderField.field.onChange, sortOrder]
  );

  return { handleOrderChange, sortOrder, isOrderDisabled: !!isSearchActive };
};

export const auxiliaryFields = {
  limit: { name: 'limit', defaultValue: '20' },
  offset: { name: 'offset', defaultValue: '0' },
  order: { name: 'order', defaultValue: { key: '', isAscending: false } },
};

export const auxiliaryFieldsTransormers = [
  new GenericFieldTransformer(auxiliaryFields.limit.name),
  new GenericFieldTransformer(auxiliaryFields.offset.name),
  new OrderFieldTransformer(auxiliaryFields.order.name),
];

export const auxiliaryFieldsDefaultValues = {
  limit: auxiliaryFields.limit.defaultValue,
  offset: auxiliaryFields.offset.defaultValue,
  order: auxiliaryFields.order.defaultValue,
};
export const themeFieldValues = ['true'];
export const themeFieldValueLabels = [];
